import { useMutation } from '@tanstack/react-query'
import { Alert, App as AntdApp, Flex, Modal, Typography } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessListInfiniteOptions,
  organizationsOrganizationsTransferOwnershipCreateMutation,
  OrganizationRole,
  type OrganizationsAccessListData,
  type PaginatedOrganizationUsersList
} from 'src/api/main'
import { InfiniteSelect } from 'src/components'
import { useUpdateUserContext } from 'src/features/organizations/hooks'
import type { WorkspaceUser } from 'src/features/users/types'
import { composeDisplayName } from 'src/features/users/utils'
import { useShowApiErrors, type OptionTransformType } from 'src/utils'

const { Text } = Typography

type Props = {
  open: boolean
  workspaceId: number
  onClose: () => void
}

type NewOwner = { id: number; label: string }

/**
 * Hands the workspace over to another member
 * (`POST /organizations/organizations/{id}/transfer-ownership/`). Owner-only
 * and irreversible from this side: the signed-in user drops to admin, so the
 * session is refreshed afterwards to re-gate the UI.
 */
export const TransferOwnershipModal = ({
  open,
  workspaceId,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const { updateUser } = useUpdateUserContext()

  const [newOwner, setNewOwner] = useState<NewOwner | null>(null)

  const { mutateAsync, isPending } = useMutation(
    organizationsOrganizationsTransferOwnershipCreateMutation()
  )

  const close = () => {
    setNewOwner(null)
    onClose()
  }

  const transfer = async () => {
    if (!newOwner) return

    try {
      await mutateAsync({
        path: { id: workspaceId },
        body: { new_owner_id: newOwner.id }
      })
      // The signed-in user just dropped from owner to admin. Re-read the session
      // user (this clears the query cache and refetches the profile) so
      // `isOwner` recomputes and every owner-only surface — the danger zone
      // included — disappears at once, instead of lingering until a hard reload.
      await updateUser({})
      message.success(
        t('workspace.settings.danger.transferSuccess', { name: newOwner.label })
      )
      close()
    } catch (error) {
      showApiErrors(error)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={close}
      onOk={() => void transfer()}
      title={t('workspace.settings.danger.transferTitle')}
      okText={t('workspace.settings.danger.transferButton')}
      okButtonProps={{ danger: true, disabled: !newOwner, loading: isPending }}
      cancelButtonProps={{ disabled: isPending }}
      destroyOnHidden
    >
      <Flex vertical gap={12}>
        <Alert
          type="warning"
          showIcon
          title={t('workspace.settings.danger.transferConfirmDescription')}
        />
        <Flex vertical gap={4}>
          <Text strong style={{ fontSize: 13 }}>
            {t('workspace.settings.danger.transferMemberLabel')}
          </Text>
          <InfiniteSelect<
            OrganizationsAccessListData,
            PaginatedOrganizationUsersList,
            WorkspaceUser
          >
            allowClear
            value={newOwner?.id}
            onChange={(value, option) =>
              setNewOwner(
                typeof value === 'number' && !Array.isArray(option)
                  ? { id: value, label: String(option?.label ?? value) }
                  : null
              )
            }
            getQueryFunction={organizationsAccessListInfiniteOptions}
            baseOptions={{ query: { organization: workspaceId } }}
            getOptions={({ options }: OptionTransformType<WorkspaceUser>) =>
              options
                .filter((u) => u.role !== OrganizationRole.OWNER)
                .map((u) => ({
                  value: u.user.id,
                  label: `${composeDisplayName(
                    u.user.first_name,
                    u.user.last_name,
                    u.user.email
                  )} (${u.user.email})`
                }))
            }
            placeholder={t(
              'workspace.settings.danger.transferMemberPlaceholder'
            )}
          />
        </Flex>
      </Flex>
    </Modal>
  )
}
